<template>
  <div class="page-container">
    <div class="container-wrapper">
      <div class="page-title-container">
        <div class="page-title">
          <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">
            {{ isEdit ? '카테고리 수정' : '카테고리 등록' }}
          </h3>
          <span class="pageDesc">{{ isEdit ? '카테고리 정보를 수정합니다.' : '새로운 카테고리를 등록합니다.' }}</span>
        </div>
      </div>
    </div>

    <div class="container-wrapper form-container">
      <!-- 기본 정보 섹션 -->
      <div class="form-section">
        <h2 class="section-title">기본 정보</h2>

        <div class="form-row">
          <div class="form-group">
            <label for="categoryName" class="form-label">카테고리명 <span class="required-mark">*</span></label>
            <input type="text" class="form-control" id="categoryName" v-model="categoryForm.name" />
          </div>
          <div class="form-group">
            <label for="categoryCode" class="form-label">카테고리 코드 <span class="required-mark">*</span></label>
            <div class="code-input-group">
              <a-tooltip 
                v-if="isEdit" 
                placement="top" 
                title="카테고리 코드는 생성 후 수정이 불가능합니다."
              >
                <input 
                  type="text" 
                  class="form-control" 
                  id="categoryCode" 
                  v-model="categoryForm.code" 
                  @input="categoryForm.code = categoryForm.code.toUpperCase(); codeVerified = false"
                  :disabled="isEdit || isLoading || isCodeChecking"
                />
              </a-tooltip>
              <input 
                v-else
                type="text" 
                class="form-control" 
                id="categoryCode" 
                v-model="categoryForm.code" 
                @input="categoryForm.code = categoryForm.code.toUpperCase(); codeVerified = false"
                :disabled="isLoading || isCodeChecking"
              />
              <a-button 
                v-if="!isEdit"
                type="primary"
                class="code-check-button" 
                :loading="isCodeChecking"
                :disabled="!categoryForm.code || isLoading" 
                @click="checkCodeDuplicate"
              >
                중복확인
              </a-button>
              <a-tooltip 
                v-else 
                placement="top" 
                title="카테고리 코드는 생성 후 변경할 수 없습니다."
              >
                <a-button 
                  type="primary"
                  class="code-check-button" 
                  disabled
                >
                  중복확인
                </a-button>
              </a-tooltip>
            </div>
            <div v-if="codeCheckMessage" :class="['code-check-result', codeVerified ? 'success' : 'error']">
              {{ codeCheckMessage }}
            </div>
            <small class="form-text text-muted">영문자(대문자), 숫자, 언더스코어(_)만 사용 가능하며, 시스템 내에서 카테고리를 식별하는 고유 코드입니다.</small>
          </div>

          <div class="form-group full-width">
            <label for="categoryDesc" class="form-label">설명</label>
            <textarea
              class="form-control"
              id="categoryDesc"
              rows="1"
              v-model="categoryForm.description"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="categoryDept" class="form-label">진료과목 <span class="required-mark">*</span></label>
            <select class="form-control" id="categoryDept" v-model="categoryForm.department">
              <option value>선택</option>
              <option :value="MedicalDept.OPH">{{ MedicalDeptText.OPH }}</option>
              <option :value="MedicalDept.DENT">{{ MedicalDeptText.DENT }}</option>
              <option :value="MedicalDept.DERM">{{ MedicalDeptText.DERM }}</option>
              <option :value="MedicalDept.PS">{{ MedicalDeptText.PS }}</option>
              <option :value="MedicalDept.VET_COMMON">{{ MedicalDeptText.VET_COMMON }}</option>
              <option :value="MedicalDept.OS">{{ MedicalDeptText.OS }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- 다국어 이름 섹션 -->
      <div class="form-section">
        <h2 class="section-title">다국어 이름</h2>
        <p class="section-desc">각 언어별 이름을 입력하세요. 입력하지 않으면 기본 이름이 사용됩니다.</p>

        <div class="form-row">
          <div class="form-group">
            <label for="categoryNameKo" class="form-label">한국어 (Korean)</label>
            <input type="text" class="form-control" id="categoryNameKo" v-model="categoryForm.names.ko" />
          </div>

          <div class="form-group">
            <label for="categoryNameEn" class="form-label">영어 (English)</label>
            <input type="text" class="form-control" id="categoryNameEn" v-model="categoryForm.names.en" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="categoryNameJa" class="form-label">일본어 (Japanese)</label>
            <input type="text" class="form-control" id="categoryNameJa" v-model="categoryForm.names.ja" />
          </div>

          <div class="form-group">
            <label for="categoryNameZh" class="form-label">중국어 (Chinese)</label>
            <input type="text" class="form-control" id="categoryNameZh" v-model="categoryForm.names.zh" />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h2 class="section-title">사용 여부</h2>

        <div class="form-row">
          <div class="form-group">
            <div class="form-label"></div>
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="useCertificate"
                v-model="categoryForm.useCertificate"
              />
              <label class="form-check-label" for="useCertificate">인증서 사용 여부</label>
            </div>
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="useHospital"
                v-model="categoryForm.useHospital"
              />
              <label class="form-check-label" for="useHospital">병원 예약 사용 여부</label>
            </div>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h2 class="section-title">병원 설정</h2>
        <p class="section-desc">이 카테고리를 어떤 병원에 노출할지 설정합니다.</p>

        <div class="form-row">
          <div class="form-group">
            <div class="form-label">노출 방식</div>
            <div class="hospital-mode-options">
              <!-- 그룹 병원 관리 모드 안내 툴팁 -->
              <a-tooltip 
                v-if="isEdit && categoryForm.hospitalMode === 'other'" 
                placement="topLeft" 
                title="그룹 병원 관리 모드는 노출 방식 수정이 불가능합니다. 삭제후 다시 등록해주세요."
                class="warning-tooltip"
              >
                <i class="fa-solid fa-circle-exclamation warning-icon"></i>
              </a-tooltip>
              
              <a-radio-group 
                v-model:value="categoryForm.hospitalMode"
                :disabled="isEdit && categoryForm.hospitalMode === 'other'"
              >
                <a-tooltip placement="top" title="모든 병원에 이 카테고리를 노출합니다.">
                  <a-radio value="all">
                    <span class="radio-label"><i class="fa-solid fa-globe"></i> 모든 병원</span>
                  </a-radio>
                </a-tooltip>
                <a-tooltip placement="top" title="선택한 병원에만 이 카테고리를 노출합니다. (최대 10개)">
                  <a-radio value="positive">
                    <span class="radio-label"><i class="fa-solid fa-check-circle"></i> 선택 병원만 노출</span>
                  </a-radio>
                </a-tooltip>
                <a-tooltip placement="top" title="선택한 병원을 제외한 모든 병원에 이 카테고리를 노출합니다. (최대 10개)">
                  <a-radio value="negative">
                    <span class="radio-label"><i class="fa-solid fa-ban"></i> 선택 병원 제외</span>
                  </a-radio>
                </a-tooltip>
                <a-tooltip placement="top" title="대량의 병원을 효율적으로 관리합니다. 제조사 소속 전체 병원을 포함할 수 있습니다.">
                  <a-radio value="other">
                    <span class="radio-label"><i class="fa-solid fa-building-user"></i> 그룹 병원 관리</span>
                  </a-radio>
                </a-tooltip>
              </a-radio-group>
            </div>
          </div>
        </div>

        <div class="form-row" v-if="categoryForm.hospitalMode === 'positive' || categoryForm.hospitalMode === 'negative' || categoryForm.hospitalMode === 'other'">
          <div class="form-group full-width">
            <div class="hospital-select-actions">
              <a-button type="primary" @click="openHospitalSelectModal" class="yellow-button">
                <span>
                  {{ categoryForm.hospitalMode === 'positive' 
                    ? '노출할 병원 추가' 
                    : categoryForm.hospitalMode === 'negative' 
                      ? '제외할 병원 추가' 
                      : '그룹 병원 추가' }}
                </span>
              </a-button>
              <span class="hospital-count" v-if="selectedHospitals.length > 0">
                {{ selectedHospitals.length }}곳의 
                {{ categoryForm.hospitalMode === 'positive' 
                  ? '노출' 
                  : categoryForm.hospitalMode === 'negative' 
                    ? '제외'
                    : '제조사 소속 병원에 선택됨' }}
              </span>
              
              <!-- 병원 초기화 버튼 -->
              <a-button 
                v-if="selectedHospitals.length > 0" 
                type="link" 
                @click="clearSelectedHospitals" 
                class="clear-hospitals-button" 
                size="small"
              >
                <i class="fa-solid fa-trash-can"></i> 모두 제거
              </a-button>
            </div>
            
            <!-- 선택된 병원 목록 테이블 -->
            <div class="selected-hospitals-table" v-if="selectedHospitals.length > 0">
              <a-table
                :dataSource="selectedHospitals"
                :columns="hospitalColumns"
                :pagination="false"
                size="small"
                :rowKey="record => record.id"
                style="max-height: 300px; overflow-y: auto;"
                :key="'hospital-table-' + selectedHospitals.length"
              >
                <template #bodyCell="{ column, record, index }">
                  <template v-if="column.key === 'index'">
                    {{ index + 1 }}
                  </template>
                  <template v-if="column.key === 'name'">
                    <div v-if="record.isAllHospitals" class="manufacturer-all-hospitals">
                      <i class="fa-solid fa-building-user"></i>
                      <span class="manufacturer-name">{{ record.name }}</span>
                      <a-tag color="gold">제조사 소속 모든 병원</a-tag>
                    </div>
                    <span v-else>{{ record.name }}</span>
                  </template>
                  <template v-if="column.key === 'address'">
                    <span v-if="record.isAllHospitals" class="manufacturer-description">
                      {{ record.name }} 제조사의 모든 병원에 이 카테고리가 노출됩니다.
                    </span>
                    <span v-else>{{ record.address }}</span>
                  </template>
                  <template v-if="column.key === 'action'">
                    <a-button type="link" danger @click="removeHospital(record)">
                      <i class="fa-solid fa-trash-can"></i>
                    </a-button>
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>

      <!-- 병원 설정 참고 사항 -->
      <div class="form-section info-section" v-if="categoryForm.hospitalMode !== 'all'">
        <div class="info-box">
          <h3 class="info-title">
            <i class="fa-solid fa-circle-info"></i> 
            {{ categoryForm.hospitalMode === 'positive' 
              ? '선택 병원만 노출' 
              : categoryForm.hospitalMode === 'negative' 
                ? '선택 병원 제외' 
                : '그룹 병원 관리' }} 모드 안내
          </h3>
          <ul class="info-list">
            <li v-if="categoryForm.hospitalMode === 'positive'">
              선택한 병원에만 이 카테고리가 노출됩니다.
            </li>
            <li v-if="categoryForm.hospitalMode === 'negative'">
              선택한 병원을 제외한 모든 병원에 이 카테고리가 노출됩니다.
            </li>
            <li v-if="categoryForm.hospitalMode === 'other'">
              대량의 병원을 매핑 테이블로 관리합니다. 제조사 연결 병원 전체 또는 일부를 포함/제외할 수 있습니다.
            </li>
            <li v-if="categoryForm.hospitalMode === 'other'">
              수백, 수천 개의 병원을 개별 추가 없이 효율적으로 관리할 수 있습니다.
            </li>
            <li v-if="categoryForm.hospitalMode === 'other'">
              별도의 매핑 테이블로 관리되어 병원 그룹 변경 시 자동으로 반영됩니다.
            </li>
            <li v-if="categoryForm.hospitalMode === 'positive' || categoryForm.hospitalMode === 'negative'">
              병원 목록은 병원 추가 버튼을 클릭하여 관리할 수 있습니다.
            </li>
            <li>
              <span class="auto-dedupe-notice"><i class="fa-solid fa-robot"></i> 제조사간 중복된 병원은 자동으로 중복이 제거되어 처리됩니다.</span>
            </li>
          </ul>
        </div>
      </div>
      
      <!-- 색상 설정 섹션 -->
      <div class="form-section">
        <h2 class="section-title">폰트 색상</h2>

        <div class="form-row">
          <div class="form-group">
            <label for="activeFontColor" class="form-label">활성</label>
            <div class="color-field">
              <input
                type="color"
                class="color-picker"
                id="activeFontColorPicker"
                v-model="categoryForm.activeFontColor"
              />
              <input
                type="text"
                class="form-control"
                id="activeFontColor"
                v-model="categoryForm.activeFontColor"
              />
              <div class="color-preview" :style="{backgroundColor: categoryForm.activeFontColor}"></div>
            </div>
          </div>

          <div class="form-group">
            <label for="inactiveFontColor" class="form-label">비활성</label>
            <div class="color-field">
              <input
                type="color"
                class="color-picker"
                id="inactiveFontColorPicker"
                v-model="categoryForm.inactiveFontColor"
              />
              <input
                type="text"
                class="form-control"
                id="inactiveFontColor"
                v-model="categoryForm.inactiveFontColor"
              />
              <div class="color-preview" :style="{backgroundColor: categoryForm.inactiveFontColor}"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 아이콘 설정 섹션 -->
      <div class="form-section">
        <h2 class="section-title">아이콘</h2>

        <div class="form-row">
          <div class="form-group">
            <image-upload
              label="활성"
              v-model:value="categoryForm.activeImage"
              @change="handleActiveImageChange"
              container-name="certilife"
              :upload-path="uploadPath"
              @upload-success="handleActiveImageUploadSuccess"
              @upload-error="handleImageUploadError"
            />
          </div>

          <div class="form-group">
            <image-upload
              label="비활성"
              v-model:value="categoryForm.inactiveImage"
              @change="handleInactiveImageChange"
              container-name="certilife"
              :upload-path="uploadPath"
              @upload-success="handleInactiveImageUploadSuccess"
              @upload-error="handleImageUploadError"
            />
          </div>
        </div>
      </div>

      <div class="form-actions">
        <a-button @click="handleCancel">취소</a-button>
        <a-button
          type="primary"
          :loading="loading"
          @click="handleSubmit"
          class="yellow-button"
        >{{ isEdit ? '수정' : '등록' }}</a-button>
      </div>
    </div>
  </div>

  <!-- 병원 검색 모달 -->
  <search-hospital-modal
    v-model:open="hospitalModalVisible"
    :category-id="categoryId"
    @select-hospitals="handleSelectHospitals"
    @close-modal="handleHospitalModalClose"
  />
</template>

<script>
import { openDialog, twoBtnDialog } from "@/utils/utils";
import { MedicalDept, MedicalDeptText } from "@/utils/enums";
import ImageUpload from "@/views/components/ImageUpload.vue";
import SearchHospitalModal from "./components/SearchHospitalModal.vue";
import { CategoryApi } from "./api";

export default {
  name: "CategoryForm",
  components: {
    ImageUpload,
    SearchHospitalModal
  },
  data() {
    return {
      MedicalDept,
      MedicalDeptText,
      loading: false,
      uploadPath: "product-v2/categories",
      categoryId: "",
      
      // 코드 중복 체크 관련 상태
      isCodeChecking: false,
      codeVerified: false,
      codeCheckMessage: "",
      
      categoryForm: {
        id: "",
        name: "",
        names: {
          ko: "",
          en: "",
          ja: "",
          zh: ""
        },
        code: "",
        description: "",
        department: "",
        useCertificate: false,
        useHospital: false,
        activeFontColor: "#2196F3",
        inactiveFontColor: "#90CAF9",
        activeImage: "",
        inactiveImage: "",
        hospitalMode: "all",
        order: 1
      },
      selectedHospitals: [],
      hospitalModalVisible: false,
      hospitalColumns: [
        {
          title: '번호',
          key: 'index',
          width: '60px',
          align: 'center'
        },
        {
          title: '병원명',
          dataIndex: 'name',
          key: 'name',
          width: '380px',
          align: 'left',
          ellipsis: true
        },
        {
          title: '주소',
          dataIndex: 'address',
          key: 'address',
          align: 'left',
          ellipsis: true
        },
        {
          title: '관리',
          key: 'action',
          width: '80px',
          align: 'center'
        }
      ]
    };
  },
  computed: {
    isEdit() {
      return !!this.categoryId;
    }
  },
  created() {
    // 라우트 파라미터에서 카테고리 ID 가져오기
    this.categoryId = this.$route.params.id;
    
    // 편집 모드인 경우 카테고리 데이터 불러오기
    if (this.categoryId) {
      this.fetchCategoryData();
    }
  },
  watch: {
    'categoryForm.code': function(newValue) {
      // 코드가 변경되면 중복 확인 상태 초기화
      if (newValue && this.codeVerified) {
        this.codeVerified = false;
        this.codeCheckMessage = "";
      }
    },
    
    'categoryForm.hospitalMode': function(newValue, oldValue) {
      // 만약 positive/negative에서 other로 변경되면 경고 표시 안함
      if ((oldValue === 'positive' || oldValue === 'negative') && newValue === 'other') {
        return;
      }
      
      // all 모드로 변경 시 선택된 병원 초기화
      if (newValue === 'all') {
        this.selectedHospitals = [];
      }
      
      // other에서 positive/negative로 변경 시, 병원이 10개 이상이면 경고 표시
      if (newValue === 'positive' || newValue === 'negative') {
        if (this.selectedHospitals.length > 10) {
          this.$confirm({
            title: '선택 병원 수 초과',
            content: `현재 ${this.selectedHospitals.length}개의 병원이 선택되어 있습니다. '선택 병원만 노출'/'선택 병원 제외' 모드에서는 최대 10개까지만 선택 가능합니다. 처음 10개만 유지하시겠습니까?`,
            okText: '예',
            cancelText: '이전 모드로 돌아가기',
            onOk: () => {
              // 처음 10개만 유지
              this.selectedHospitals = this.selectedHospitals.slice(0, 10);
            },
            onCancel: () => {
              // 이전 모드로 돌아가기
              this.categoryForm.hospitalMode = oldValue;
            }
          });
        }
      }
    }
  },
  methods: {
    // 카테고리 데이터 가져오기
    async fetchCategoryData() {
      try {
        this.loading = true;
        const response = await CategoryApi.getCategory(this.categoryId);
        const categoryData = response.data;
        
        // 기본 폼 데이터 설정
        this.categoryForm = {
          id: categoryData._id,
          name: categoryData.name || "",
          names: {
            ko: categoryData.names?.ko || "",
            en: categoryData.names?.en || "",
            ja: categoryData.names?.ja || "",
            zh: categoryData.names?.zh || ""
          },
          code: categoryData.code || "",
          description: categoryData.description || "",
          department: categoryData.department || "",
          useCertificate: categoryData.useCertificate || false,
          useHospital: categoryData.useHospital || false,
          activeFontColor: categoryData.activeFontColor || "#2196F3",
          inactiveFontColor: categoryData.inactiveFontColor || "#90CAF9",
          activeImage: categoryData.activeImage || "",
          inactiveImage: categoryData.inactiveImage || "",
          hospitalMode: categoryData.company?.mode || "all",
          order: categoryData.order || 1
        };
        
        // 수정 모드에서는 기존 코드를 이미 검증된 상태로 설정
        if (this.categoryForm.code) {
          this.codeVerified = true;
          this.codeCheckMessage = `카테고리 코드 '${this.categoryForm.code}'는 사용 가능합니다.`;
        }
        
        // 병원 목록 초기화
        this.selectedHospitals = [];
        
        // 병원 목록 처리
        if (categoryData.company && 
            (categoryData.company.mode === 'positive' || categoryData.company.mode === 'negative' || categoryData.company.mode === 'other') && 
            categoryData.company.list && 
            categoryData.company.list.length > 0) {
          
          // company 객체의 list 배열에서 병원 정보 추출
          this.selectedHospitals = categoryData.company.list.map(item => {
            // 제조사 전체 병원인 경우
            if (item.isAllHospitals && item.manufacturerId) {
              const manufacturerName = item.manufacturerName || item.companyName || '선택된 제조사';
              return {
                id: 'ALL_' + item.manufacturerId,
                name: manufacturerName,
                isAllHospitals: true,
                manufacturerId: item.manufacturerId,
                manufacturerName: manufacturerName,
                companyId: item.companyId || item.manufacturerId // companyId가 없으면 manufacturerId 사용
              };
            }
            
            // 일반 병원인 경우
            const hospital = {
              id: item.companyId,
              name: item.companyName,
              address: item.companyAddress || '',
              isAllHospitals: false,
              companyId: item.companyId // companyId 추가
            };
            
            // 제조사 정보 추가 (ValidationError 방지)
            hospital.manufacturerId = item.manufacturerId || '';
            hospital.manufacturerName = item.manufacturerName || '독립병원';
            
            return hospital;
          });
        }
      } catch (error) {
        console.error('카테고리 데이터 불러오기 실패:', error);
        openDialog("카테고리 데이터를 불러오는데 실패했습니다.", "error", "확인");
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * 카테고리 코드 중복 확인
     */
    async checkCodeDuplicate() {
      // 코드 입력 확인
      if (!this.categoryForm.code) {
        this.$message.warning('카테고리 코드를 입력해주세요.');
        return;
      }
      
      // 코드 형식 검사 (영문 대문자, 숫자, 언더스코어만 허용)
      const codeRegex = /^[A-Z0-9_]+$/;
      if (!codeRegex.test(this.categoryForm.code)) {
        this.codeCheckMessage = '카테고리 코드는 영문자(대문자), 숫자, 언더스코어(_)만 포함해야 합니다.';
        this.codeVerified = false;
        return;
      }
      
      try {
        this.isCodeChecking = true;
        
        // 제외할 카테고리 ID (수정 모드에서만 사용)
        const excludeId = this.isEdit ? this.categoryForm.id : undefined;
        
        // API 호출
        const response = await CategoryApi.checkCodeDuplicate(this.categoryForm.code, excludeId);
        const result = response.data;
        
        // 결과 처리
        this.codeCheckMessage = result.message;
        this.codeVerified = !result.isDuplicate;
        
        if (this.codeVerified) {
          this.$message.success(result.message);
        } else {
          this.$message.error(result.message);
        }
      } catch (error) {
        console.error('카테고리 코드 중복 확인 중 오류:', error);
        this.codeCheckMessage = '코드 중복 확인 중 오류가 발생했습니다.';
        this.codeVerified = false;
        this.$message.error('코드 중복 확인 중 오류가 발생했습니다.');
      } finally {
        this.isCodeChecking = false;
      }
    },
    
    /**
     * 폼 제출 처리
     */
    async handleSubmit() {
      // 필수 필드 검증
      if (!this.categoryForm.name) {
        openDialog("카테고리명을 입력해주세요.", "warning", "확인");
        return;
      }
      
      // 카테고리 코드 필수 검증
      if (!this.categoryForm.code) {
        openDialog("카테고리 코드를 입력해주세요.", "warning", "확인");
        return;
      }
      
      // 코드 중복 확인 여부 검증 (신규 생성 시에만 필수)
      if (!this.isEdit && !this.codeVerified) {
        openDialog("카테고리 코드 중복 확인을 해주세요.", "warning", "확인");
        return;
      }
      
      // 코드 형식 검증 (영문 대문자, 숫자, 언더스코어만 허용)
      const codeRegex = /^[A-Z0-9_]+$/;
      if (!codeRegex.test(this.categoryForm.code)) {
        openDialog("카테고리 코드는 영문자(대문자), 숫자, 언더스코어(_)만 포함해야 합니다.", "warning", "확인");
        return;
      }

      // 편집 모드가 아닌 경우(등록 모드) 진료과목 검증
      if (!this.isEdit && !this.categoryForm.department) {
        openDialog("진료과목을 선택해주세요.", "warning", "확인");
        return;
      }
      
      // 병원 모드가 positive나 negative인데 병원이 선택되지 않은 경우 검증
      if ((this.categoryForm.hospitalMode === 'positive' || this.categoryForm.hospitalMode === 'negative') && 
          this.selectedHospitals.length === 0) {
        openDialog("병원을 한 개 이상 선택해주세요.", "warning", "확인");
        return;
      }

      try {
        this.loading = true;

        // 폼 데이터 준비
        const formData = {
          name: this.categoryForm.name,
          names: this.categoryForm.names,
          code: this.categoryForm.code,
          description: this.categoryForm.description,
          department: this.categoryForm.department,
          useCertificate: this.categoryForm.useCertificate,
          useHospital: this.categoryForm.useHospital,
          activeFontColor: this.categoryForm.activeFontColor,
          inactiveFontColor: this.categoryForm.inactiveFontColor,
          activeImage: this.categoryForm.activeImage,
          inactiveImage: this.categoryForm.inactiveImage,
        };
        
        // 수정 모드일 때만 기존 order 값을 포함, 신규 생성 시에는 백엔드에서 자동 계산됨
        if (this.isEdit && this.categoryForm.order) {
          formData.order = this.categoryForm.order;
        }
        
        // 병원 관련 데이터 추가 - DTO 형식에 맞게 수정
        if (this.categoryForm.hospitalMode) {
          formData.company = {
            mode: this.categoryForm.hospitalMode,
            list: []
          };
          
          // 긍정 또는 부정 모드에서만 병원 목록 추가
          if ((this.categoryForm.hospitalMode === 'positive' || this.categoryForm.hospitalMode === 'negative' || this.categoryForm.hospitalMode === 'other') 
              && this.selectedHospitals.length > 0) {
            formData.company.list = this.selectedHospitals.map(hospital => {
              // 제조사 전체 병원 선택인 경우 특별 처리
              if (hospital.isAllHospitals) {
                return {
                  companyId: hospital.manufacturerId,
                  manufacturerId: hospital.manufacturerId,
                  manufacturerName: hospital.name || '알 수 없는 제조사',
                  isAllHospitals: true
                };
              }
              
              // 개별 병원 선택인 경우
              const item = {
                companyId: hospital.id,
                isAllHospitals: false
              };
              
              // 제조사 정보가 있으면 추가 (이 부분이 누락되어 ValidationError 발생)
              if (hospital.manufacturerId) {
                item.manufacturerId = hospital.manufacturerId;
                item.manufacturerName = hospital.manufacturerName || hospital.name || '독립병원';
              } else {
                // 빈 문자열이라도 manufacturerId를 제공하여 유효성 검사 통과
                item.manufacturerId = '';
                item.manufacturerName = '독립병원';
              }
              
              return item;
            });
          }
        }

        let response;
        if (this.isEdit) {
          // 수정 모드: PUT 요청
          response = await CategoryApi.updateCategory(this.categoryForm.id, formData);

          // 성공 메시지 
          await openDialog("카테고리가 수정되었습니다.", "success", "확인");
        } else {
          // 등록 모드: POST 요청
          response = await CategoryApi.createCategory(formData);

          // 성공 메시지
          await openDialog("카테고리가 등록되었습니다.", "success", "확인");
        }
        
        // 목록 페이지로 이동
        this.$router.go(-1);
      } catch (error) {
        console.error(`카테고리 ${this.isEdit ? '수정' : '등록'} 실패:`, error);
        
        // 에러 메시지 처리 개선
        let errorMessage = `카테고리 ${this.isEdit ? '수정' : '등록'} 중 오류가 발생했습니다.`;
        
        // 서버 응답 확인
        if (error.response && error.response.data) {
          const serverError = error.response.data;
          
          // 코드 중복 에러 감지
          if (serverError.message && serverError.message.includes('카테고리 코드')) {
            errorMessage = serverError.message;
          } else if (serverError.message && serverError.message.includes('duplicate key error')) {
            errorMessage = `카테고리 코드 '${this.categoryForm.code}'는 이미 사용 중입니다. 다른 코드를 입력해 주세요.`;
          } else if (serverError.message) {
            errorMessage = serverError.message;
          }
        }
        
        openDialog(errorMessage, "error", "확인");
      } finally {
        this.loading = false;
      }
    },

    // 취소 처리
    handleCancel() {
      this.$router.go(-1); // 이전 페이지로 이동
    },

    // 활성 이미지 변경 처리
    handleActiveImageChange(imageData) {
      this.categoryForm.activeImage = imageData;
    },

    // 비활성 이미지 변경 처리
    handleInactiveImageChange(imageData) {
      this.categoryForm.inactiveImage = imageData;
    },

    // 이미지 업로드 성공 핸들러
    handleActiveImageUploadSuccess(response) {
      try {
        console.log("활성 이미지 업로드 성공", response);
        if (response.data && response.data.results && response.data.results.length > 0) {
          this.categoryForm.activeImage = response.data.results[0].url;
        }
      } catch (error) {
        console.error("이미지 URL 처리 오류", error);
      }
    },

    handleInactiveImageUploadSuccess(response) {
      try {
        console.log("비활성 이미지 업로드 성공", response);
        if (response.data && response.data.results && response.data.results.length > 0) {
          this.categoryForm.inactiveImage = response.data.results[0].url;
        }
      } catch (error) {
        console.error("이미지 URL 처리 오류", error);
      }
    },

    // 업로드 오류 핸들러
    handleImageUploadError(error) {
      console.error("이미지 업로드 실패", error);
      openDialog("이미지 업로드에 실패했습니다.", "error", "확인");
    },

    /**
     * 병원 선택 모달 열기
     */
    openHospitalSelectModal() {
      // positive 또는 negative 모드에서 10개 제한 체크
      if ((this.categoryForm.hospitalMode === 'positive' || this.categoryForm.hospitalMode === 'negative') && 
          this.selectedHospitals.length >= 10) {
        this.$message.warning('최대 10개까지만 선택할 수 있습니다. 더 많은 병원을 관리하려면 \'그룹 병원 관리\' 모드를 사용하세요.');
        return;
      }
      
      this.hospitalModalVisible = true;
    },

    /**
     * 선택된 병원 초기화
     */
    async clearSelectedHospitals() {
      if (this.selectedHospitals.length === 0) return;
      
      const isConfirmed = await twoBtnDialog(
        '선택된 병원 모두 제거', 
        '정말로 선택된 모든 병원을 제거하시겠습니까?', 
        '제거'
      );
      
      if (isConfirmed) {
        this.selectedHospitals = [];
        this.$message.success('선택된 병원이 모두 제거되었습니다.');
      }
    },
    
    /**
     * 선택된 병원 처리
     */
    async handleSelectHospitals(hospitals) {
      if (!hospitals || hospitals.length === 0) return;
      
      // 제조사 소속 모든 병원 선택 처리
      const hasAllHospitals = hospitals.some(hospital => hospital.isAllHospitals);
      
      // 제조사 소속 모든 병원이 선택된 경우
      if (hasAllHospitals) {
        const manufacturerHospital = hospitals.find(h => h.isAllHospitals);
        
        // manufacturerId가 없는 경우 오류 처리
        if (!manufacturerHospital.manufacturerId) {
          this.$message.error('제조사 ID 정보가 누락되었습니다. 관리자에게 문의하세요.');
          return;
        }
        
        // 이미 동일한 제조사 전체가 선택되어 있는지 확인
        const existingAllHospital = this.selectedHospitals.find(h => 
          h.isAllHospitals && h.manufacturerId === manufacturerHospital.manufacturerId
        );
        
        if (!existingAllHospital) {
          // 제조사 정보가 명확하게 포함되도록 수정
          this.selectedHospitals.push({
            id: 'ALL_' + manufacturerHospital.manufacturerId,
            name: manufacturerHospital.name,
            isAllHospitals: true,
            manufacturerId: manufacturerHospital.manufacturerId,
            manufacturerName: manufacturerHospital.name,
            companyId: manufacturerHospital.manufacturerId // DTO 요구사항을 위한 companyId 필드 추가
          });
          this.$message.success(`${manufacturerHospital.name} 제조사의 모든 병원이 추가되었습니다.`);
        } else {
          this.$message.info(`${manufacturerHospital.name} 제조사의 모든 병원은 이미 추가되어 있습니다.`);
        }
        
        return;
      }
      
      // positive 또는 negative 모드에서 10개 제한 체크
      if ((this.categoryForm.hospitalMode === 'positive' || this.categoryForm.hospitalMode === 'negative') && 
          this.selectedHospitals.length + hospitals.length > 10) {
        this.$message.warning(`최대 10개까지만 선택할 수 있습니다. 현재 ${this.selectedHospitals.length}개가 선택되어 있습니다.`);
        return;
      }
      
      // 새로 선택된 병원 추가 (이미 존재하는 병원은 추가하지 않음)
      const uniqueNewHospitals = this.filterUniqueHospitals(hospitals);
      
      if (uniqueNewHospitals.length > 0) {
        // 새로 추가하는 병원에 manufacturerId와 manufacturerName 정보 보강
        const enhancedHospitals = uniqueNewHospitals.map(hospital => {
          const enhancedHospital = { ...hospital };
          
          // 제조사 정보가 없는 경우 기본값 설정
          if (!enhancedHospital.manufacturerId) {
            enhancedHospital.manufacturerId = '';
            enhancedHospital.manufacturerName = '독립병원';
          }
          
          return enhancedHospital;
        });
        
        this.selectedHospitals = [...this.selectedHospitals, ...enhancedHospitals];
        
        // 중복 제거된 경우 알림
        if (hospitals.length !== uniqueNewHospitals.length) {
          this.$message.info(`${hospitals.length - uniqueNewHospitals.length}개의 중복된 병원이 제외되었습니다.`);
        }
      } else if (hospitals.length > 0) {
        // 모든 병원이 중복인 경우
        this.$message.info('이미 선택된 병원만 있어 추가되지 않았습니다.');
        return;
      }
      
      // 만약 10개를 초과하게 되면 그룹 병원 관리 모드로 전환할지 물어보기
      if ((this.categoryForm.hospitalMode === 'positive' || this.categoryForm.hospitalMode === 'negative') && 
          this.selectedHospitals.length >= 8) {
        this.$confirm({
          title: '많은 병원을 선택하셨습니다',
          content: '10개 이상의 병원을 관리하려면 \'그룹 병원 관리\' 모드로 전환하는 것이 좋습니다. 지금 전환하시겠습니까?',
          okText: '예, 전환할게요',
          cancelText: '아니오',
          onOk: () => {
            this.categoryForm.hospitalMode = 'other';
          }
        });
      }
    },
    
    /**
     * 중복된 병원 제거
     */
    filterUniqueHospitals(hospitals) {
      // 현재 선택된 병원의 ID 목록
      const existingIds = this.selectedHospitals.map(h => h.id);
      
      // 중복되지 않은 새 병원만 필터링
      return hospitals.filter(hospital => !existingIds.includes(hospital.id));
    },

    handleHospitalModalClose() {
      this.hospitalModalVisible = false;
    },

    removeHospital(hospital) {
      this.selectedHospitals = this.selectedHospitals.filter(h => h.id !== hospital.id);
    }
  }
};
</script>

<style lang="scss" scoped>
.page-container {
  padding: 1vw;
}

.form-container {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eaeaea;
}

.section-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  margin-top: -8px;
}

.form-row {
  display: flex;
  margin: 0 -10px;
  flex-wrap: wrap;

  .form-group {
    flex: 0 0 50%;
    padding: 0 10px;
    margin-bottom: 1rem;

    &.full-width {
      flex: 0 0 100%;
    }
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eaeaea;
}

.color-field {
  display: flex;
  align-items: center;

  input[type="text"] {
    flex-grow: 1;
    height: 36px;
    border-radius: 4px;
    border: 1px solid #d9d9d9;
    padding: 0 12px;
    
    &:hover, &:focus {
      border-color: #40a9ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
      outline: none;
    }
  }

  .color-preview {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    margin-left: 8px;
    border: 1px solid #ddd;
    transition: all 0.3s;
  }
  
  .color-picker {
    width: 42px;
    height: 36px;
    margin-right: 8px;
    border: none;
    padding: 0;
    background: none;
    cursor: pointer;
    
    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }
    
    &::-webkit-color-swatch {
      border: 1px solid #d9d9d9;
      border-radius: 4px;
    }
    
    &::-moz-color-swatch {
      border: 1px solid #d9d9d9;
      border-radius: 4px;
    }
  }
}

:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

.hospital-mode-options {
  margin-top: 8px;
}

:deep(.ant-radio-wrapper) {
  margin-right: 20px;
  
  .radio-label {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 4px;
      color: #FFCC32;
    }
  }
}

.hospital-select-actions {
  display: flex;
  align-items: center;
  margin-top: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.hospital-count {
  margin-left: 12px;
  font-size: 14px;
  color: #666;
}

.clear-hospitals-button {
  margin-left: auto;
  color: #ff4d4f;
  
  &:hover {
    color: #ff7875;
  }
  
  i {
    margin-right: 4px;
  }
}

.selected-hospitals-table {
  margin-top: 16px;
  padding: 2px;
  width: 100%;
  max-width: 1200px;
  border: 1px solid #eaeaea;
  border-radius: 8px;
}

.required-mark {
  color: #ff4d4f;
  margin-left: 4px;
}

.form-text {
  margin-top: 4px;
  font-size: 12px;
}

.info-section {
  margin-top: -10px;
  margin-bottom: 30px;
}

.info-box {
  background: #fffbeb;
  border-radius: 8px;
  padding: 16px 20px;
  border: 1px solid #ffedd5;
}

.info-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  
  i {
    color: #ffcc32;
    margin-right: 8px;
  }
}

.info-list {
  list-style-type: none;
  padding-left: 12px;
  margin: 0;
  
  li {
    margin-bottom: 6px;
    position: relative;
    padding-left: 16px;
    
    &:before {
      content: "•";
      position: absolute;
      left: 0;
      color: #ffcc32;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.auto-dedupe-notice {
  color: #059669;
  font-weight: 500;
  
  i {
    margin-right: 4px;
  }
}

.warning-tooltip {
  margin-bottom: 6px;
  display: block;
}

.warning-icon {
  color: #ff4d4f;
  font-size: 16px;
  margin-right: 8px;
}

.manufacturer-all-hospitals {
  display: flex;
  align-items: center;
  
  i {
    margin-right: 8px;
    color: #fa8c16;
    font-size: 16px;
  }
  
  .manufacturer-name {
    margin-right: 8px;
    font-weight: 600;
  }
}

.manufacturer-description {
  font-size: 13px;
  color: #666;
  font-style: italic;
  
  &::before {
    content: "※ ";
    color: #fa8c16;
  }
}

.code-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .form-control {
    flex: 1;
  }
  
  .code-check-button {
    height: 36px;
    background-color: #FFCC32 !important;
    border-color: #FFCC32 !important;
    color: #000 !important;

    &:hover {
      background-color: #e6b82e !important;
      border-color: #e6b82e !important;
    }

    &:disabled {
      background-color: #d9d9d9 !important;
      border-color: #d9d9d9 !important;
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }
}

.code-check-result {
  margin-top: 6px;
  font-size: 13px;
  
  &.success {
    color: #52c41a;
  }
  
  &.error {
    color: #ff4d4f;
  }
}
</style> 